import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { FileText, Plus, Calendar, Trash2, Pencil, Filter, RefreshCw, Lock, Search, Download, Maximize2, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper para formatear fechas SIN problemas de timezone
const formatDateSafe = (dateStr, options = { day: 'numeric', month: 'short', year: 'numeric' }) => {
  if (!dateStr) return 'N/A';
  const datePart = dateStr.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  const date = new Date(year, month - 1, day, 12, 0, 0);
  return date.toLocaleDateString('es-MX', options);
};

export default function Tratos() {
  const [tratos, setTratos] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [fraccionamientos, setFraccionamientos] = useState([]);
  // 🔥 ESTANDARIZADO: Usar misma clave que Dashboard y Pagos
  const [selectedFraccId, setSelectedFraccId] = useState(() => {
    return localStorage.getItem('filtro_fraccionamiento') || 'todos';
  });
  const [formFraccFilter, setFormFraccFilter] = useState(() => {
    return localStorage.getItem('filtro_fraccionamiento') || 'todos';
  });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTrato, setEditingTrato] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, trato: null });
  
  // Búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  
  // Estado de permisos
  const [userRole, setUserRole] = useState('owner');
  const canWrite = userRole === 'owner' || userRole === 'admin';
  
  // 🔥 ESTANDARIZADO: Guardar con misma clave
  const handleFraccFilter = (value) => {
    setSelectedFraccId(value);
    localStorage.setItem('filtro_fraccionamiento', value);
  };
  
  const [formData, setFormData] = useState({
    lotes_ids: [],
    cliente_id: '',
    tipo_pago: 'financiado',
    metodo_pago: 'efectivo',  // efectivo, transferencia, tarjeta, cheque
    moneda: 'USD',  // Moneda de la venta
    tipo_cambio: '',  // Tipo de cambio para conversión
    precio_final: '',
    enganche: '',
    fecha_enganche: new Date().toISOString().split('T')[0],  // Fecha del enganche
    metodo_pago_enganche: '',
    modo_calculo: 'meses_fijos',
    numero_meses: '',
    monto_mensualidad: '',
    fecha_inicio: new Date().toISOString().split('T')[0],
    fecha_primera_mensualidad: new Date().toISOString().split('T')[0],  // Fecha de la primera mensualidad - valor por defecto
    modo_fecha: 'dia_fijo',
    dia_pago: '',
    fecha_unica_pago: '',
    interes_mensual: '0',
    interes_atraso: '0',
    interes_venta: '0',
    notas: ''
  });
  const [editFormData, setEditFormData] = useState({
    lotes_ids: [],
    cliente_id: '',
    tipo_pago: 'financiado',
    metodo_pago: 'efectivo',
    moneda: 'USD',
    tipo_cambio: '',
    precio_final: '',
    enganche: '',
    fecha_enganche: '',
    metodo_pago_enganche: '',
    modo_calculo: 'meses_fijos',
    numero_meses: '',
    monto_mensualidad: '',
    fecha_inicio: '',
    fecha_primera_mensualidad: '',
    modo_fecha: 'dia_fijo',
    dia_pago: '',
    fecha_unica_pago: '',
    interes_mensual: '',
    interes_atraso: '',
    interes_venta: '',
    notas: ''
  });
  
  // Estado para pagos personalizados (para mensualidad fija)
  const [pagosPersonalizados, setPagosPersonalizados] = useState([]);
  const [mostrarPreviaPagos, setMostrarPreviaPagos] = useState(false);
  
  // Estado para desglose de pagos en edición
  const [mostrarDesglosePagosEdit, setMostrarDesglosePagosEdit] = useState(false);
  const [desglosePagosEdit, setDesglosePagosEdit] = useState([]);
  
  // Estado para tabla de amortización con interés compuesto
  const [tablaAmortizacion, setTablaAmortizacion] = useState(null);
  const [loadingAmortizacion, setLoadingAmortizacion] = useState(false);
  const [mostrarTablaAmortizacion, setMostrarTablaAmortizacion] = useState(false);
  const [modalAmortizacionAbierto, setModalAmortizacionAbierto] = useState(false); // Modal pantalla grande
  
  // Estado para tipo de cambio
  const [tipoCambioActual, setTipoCambioActual] = useState(17.50);
  const [loadingTipoCambio, setLoadingTipoCambio] = useState(false);
  const [precioOriginalLotes, setPrecioOriginalLotes] = useState(0);  // Precio original de los lotes
  const [monedaOriginalLotes, setMonedaOriginalLotes] = useState('USD');  // Moneda original de los lotes
  
  // Estado para buscador de lotes
  const [busquedaLoteCrear, setBusquedaLoteCrear] = useState('');
  const [busquedaLoteEditar, setBusquedaLoteEditar] = useState('');
  
  // Estado para buscador de clientes
  const [busquedaClienteCrear, setBusquedaClienteCrear] = useState('');
  const [busquedaClienteEditar, setBusquedaClienteEditar] = useState('');

  useEffect(() => {
    fetchData();
    fetchTipoCambio();
    fetchUserRole();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Obtener rol del usuario
  const fetchUserRole = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
      if (response.ok) {
        const userData = await response.json();
        setUserRole(userData.account_role || 'owner');
      }
    } catch (error) {
      console.error('Error al obtener usuario');
    }
  };
  
  // Obtener tipo de cambio
  const fetchTipoCambio = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tipo-cambio`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTipoCambioActual(data.tipo_cambio);
      }
    } catch (error) {
      console.error('Error al obtener tipo de cambio');
    }
  };
  
  // Actualizar tipo de cambio manualmente
  const actualizarTipoCambio = async () => {
    setLoadingTipoCambio(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/tipo-cambio`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTipoCambioActual(data.tipo_cambio);
        // Si hay conversión activa, recalcular el precio
        if (formData.tipo_cambio && precioOriginalLotes > 0) {
          let nuevoPrecio = formData.precio_final;
          if (monedaOriginalLotes === 'USD' && formData.moneda === 'MXN') {
            nuevoPrecio = Math.round(precioOriginalLotes * data.tipo_cambio * 100) / 100;
          } else if (monedaOriginalLotes === 'MXN' && formData.moneda === 'USD') {
            nuevoPrecio = Math.round(precioOriginalLotes / data.tipo_cambio * 100) / 100;
          }
          setFormData(prev => ({
            ...prev,
            tipo_cambio: data.tipo_cambio.toString(),
            precio_final: nuevoPrecio.toString()
          }));
        }
        toast.success(`Tipo de cambio: $${data.tipo_cambio} MXN/USD`);
      }
    } catch (error) {
      toast.error('Error al actualizar tipo de cambio');
    } finally {
      setLoadingTipoCambio(false);
    }
  };

  const fetchData = async () => {
    try {
      const [tratosRes, lotesRes, clientesRes, fraccsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/tratos/con-deuda`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/lotes`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/clientes`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/fraccionamientos`, { credentials: 'include' })
      ]);
      
      if (!tratosRes.ok || !lotesRes.ok || !clientesRes.ok || !fraccsRes.ok) throw new Error('Error al cargar datos');
      
      const [tratosData, lotesData, clientesData, fraccsData] = await Promise.all([
        tratosRes.json(),
        lotesRes.json(),
        clientesRes.json(),
        fraccsRes.json()
      ]);
      
      setTratos(Array.isArray(tratosData) ? tratosData : []);
      setLotes(Array.isArray(lotesData) ? lotesData : []);
      setClientes(Array.isArray(clientesData) ? clientesData : []);
      setFraccionamientos(Array.isArray(fraccsData) ? fraccsData : []);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Función para calcular vista previa de pagos con mensualidad fija
  const calcularPreviaPagos = (precioFinal, enganche, montoMensual, fechaInicio, diaPago) => {
    const montoAFinanciar = parseFloat(precioFinal || 0) - parseFloat(enganche || 0);
    const mensualidad = parseFloat(montoMensual || 0);
    
    if (montoAFinanciar <= 0 || mensualidad <= 0) return [];
    
    // Calcular número de pagos completos
    const numPagosCompletos = Math.floor(montoAFinanciar / mensualidad);
    const montoUltimoPago = montoAFinanciar - (numPagosCompletos * mensualidad);
    
    const pagos = [];
    const fechaBase = new Date(fechaInicio);
    const dia = parseInt(diaPago) || fechaBase.getDate();
    
    for (let i = 0; i < numPagosCompletos; i++) {
      // CAMBIO: El primer pago es en el MISMO mes de fecha_primera_mensualidad (sin +1)
      const fechaPago = new Date(fechaBase.getFullYear(), fechaBase.getMonth() + i, dia);
      pagos.push({
        numero: i + 1,
        monto: mensualidad,
        fecha: fechaPago.toISOString().split('T')[0],
        esAjuste: false
      });
    }
    
    // Si hay un resto, agregar el último pago ajustado
    if (montoUltimoPago > 0.01) {
      // CAMBIO: Ajustar también la fecha del último pago (sin +1 extra)
      const fechaUltimo = new Date(fechaBase.getFullYear(), fechaBase.getMonth() + numPagosCompletos, dia);
      pagos.push({
        numero: numPagosCompletos + 1,
        monto: Math.round(montoUltimoPago * 100) / 100,
        fecha: fechaUltimo.toISOString().split('T')[0],
        esAjuste: true
      });
    }
    
    return pagos;
  };

  // Actualizar vista previa cuando cambian los datos relevantes
  const actualizarPreviaPagos = () => {
    if (formData.modo_calculo === 'mensualidad_fija' && formData.precio_final && formData.monto_mensualidad) {
      // Usar fecha_primera_mensualidad si existe, sino usar fecha_inicio
      const fechaBase = formData.fecha_primera_mensualidad || formData.fecha_inicio;
      const pagos = calcularPreviaPagos(
        formData.precio_final,
        formData.enganche,
        formData.monto_mensualidad,
        fechaBase,
        formData.dia_pago
      );
      setPagosPersonalizados(pagos);
    }
  };

  // Función para calcular y mostrar tabla de amortización con interés compuesto
  const calcularTablaAmortizacion = async (silencioso = false) => {
    const precioFinal = parseFloat(formData.precio_final || 0);
    const enganche = parseFloat(formData.enganche || 0);
    const saldoAFinanciar = precioFinal - enganche;
    const tasaInteres = parseFloat(formData.interes_venta || 0);
    const pagoMensual = parseFloat(formData.monto_mensualidad || 0);
    const fechaPrimera = formData.fecha_primera_mensualidad || formData.fecha_inicio;
    const dia = formData.dia_pago ? parseInt(formData.dia_pago) : null;

    // Si no hay interés, no calcular amortización compuesta
    if (tasaInteres <= 0) {
      setTablaAmortizacion(null);
      setMostrarTablaAmortizacion(false);
      return;
    }

    if (saldoAFinanciar <= 0 || pagoMensual <= 0 || !fechaPrimera) {
      if (!silencioso) toast.error('Complete los campos: precio, enganche, mensualidad y fecha');
      return;
    }

    // Validación: pago debe cubrir al menos el interés del primer mes
    const interesPrimerMes = saldoAFinanciar * (tasaInteres / 100);
    if (pagoMensual <= interesPrimerMes) {
      if (!silencioso) toast.error(`El pago mensual ($${pagoMensual.toLocaleString()}) debe ser mayor al interés del primer mes ($${interesPrimerMes.toLocaleString('es-MX', {minimumFractionDigits: 2})})`);
      setTablaAmortizacion(null);
      setMostrarTablaAmortizacion(false);
      return;
    }

    setLoadingAmortizacion(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/tratos/preview-amortizacion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          saldo_a_financiar: saldoAFinanciar,
          tasa_interes_mensual: tasaInteres,
          pago_mensual_fijo: pagoMensual,
          fecha_primera_mensualidad: fechaPrimera,
          dia_pago: dia
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error calculando amortización');
      }

      setTablaAmortizacion(data);
      setMostrarTablaAmortizacion(true);
    } catch (error) {
      if (!silencioso) toast.error(error.message);
      setTablaAmortizacion(null);
    } finally {
      setLoadingAmortizacion(false);
    }
  };

  // Estado para mensualidad calculada en modo meses fijos
  const [mensualidadCalculada, setMensualidadCalculada] = useState(null);
  const [loadingMensualidad, setLoadingMensualidad] = useState(false);

  // Función para calcular mensualidad dado número de meses (modo meses fijos con interés)
  const calcularMensualidadPorMeses = async (silencioso = false) => {
    const precioFinal = parseFloat(formData.precio_final || 0);
    const enganche = parseFloat(formData.enganche || 0);
    const saldoAFinanciar = precioFinal - enganche;
    const tasaInteres = parseFloat(formData.interes_venta || 0);
    const numMeses = parseInt(formData.numero_meses || 0);
    const fechaPrimera = formData.fecha_primera_mensualidad || formData.fecha_inicio;

    if (saldoAFinanciar <= 0 || numMeses <= 0 || !fechaPrimera) {
      return;
    }

    setLoadingMensualidad(true);
    try {
      // Primero calcular la mensualidad
      const response = await fetch(`${BACKEND_URL}/api/tratos/calcular-mensualidad`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          saldo_a_financiar: saldoAFinanciar,
          tasa_interes_mensual: tasaInteres,
          numero_meses: numMeses
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error calculando mensualidad');
      }

      setMensualidadCalculada(data.mensualidad_calculada);

      // Ahora calcular la tabla de amortización con esa mensualidad
      const amortResponse = await fetch(`${BACKEND_URL}/api/tratos/preview-amortizacion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          saldo_a_financiar: saldoAFinanciar,
          tasa_interes_mensual: tasaInteres,
          pago_mensual_fijo: data.mensualidad_calculada,
          fecha_primera_mensualidad: fechaPrimera,
          dia_pago: formData.dia_pago ? parseInt(formData.dia_pago) : null
        })
      });

      const amortData = await amortResponse.json();
      
      if (!amortResponse.ok) {
        throw new Error(amortData.detail || 'Error calculando amortización');
      }

      setTablaAmortizacion(amortData);
      setMostrarTablaAmortizacion(true);
    } catch (error) {
      if (!silencioso) toast.error(error.message);
      setMensualidadCalculada(null);
      setTablaAmortizacion(null);
    } finally {
      setLoadingMensualidad(false);
    }
  };

  // Auto-calcular tabla de amortización cuando cambian los valores relevantes
  useEffect(() => {
    const tasaInteres = parseFloat(formData.interes_venta || 0);
    const pagoMensual = parseFloat(formData.monto_mensualidad || 0);
    const numMeses = parseInt(formData.numero_meses || 0);
    const precioFinal = parseFloat(formData.precio_final || 0);
    const enganche = parseFloat(formData.enganche || 0);
    const saldoAFinanciar = precioFinal - enganche;
    const fechaPrimera = formData.fecha_primera_mensualidad || formData.fecha_inicio;
    
    // Resetear si no hay interés
    if (tasaInteres === 0) {
      setTablaAmortizacion(null);
      setMostrarTablaAmortizacion(false);
      setMensualidadCalculada(null);
      return;
    }
    
    // MODO MESES FIJOS con interés: calcular mensualidad automáticamente
    if (tasaInteres > 0 && formData.modo_calculo === 'meses_fijos' && numMeses > 0 && saldoAFinanciar > 0 && fechaPrimera) {
      const timer = setTimeout(() => {
        calcularMensualidadPorMeses(true);
      }, 500);
      return () => clearTimeout(timer);
    }
    
    // MODO MENSUALIDAD FIJA con interés: usar mensualidad proporcionada
    if (tasaInteres > 0 && formData.modo_calculo === 'mensualidad_fija' && pagoMensual > 0 && saldoAFinanciar > 0 && fechaPrimera) {
      const interesPrimerMes = saldoAFinanciar * (tasaInteres / 100);
      if (pagoMensual > interesPrimerMes) {
        const timer = setTimeout(() => {
          calcularTablaAmortizacion(true);
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [formData.precio_final, formData.enganche, formData.interes_venta, formData.monto_mensualidad, formData.numero_meses, formData.modo_calculo, formData.fecha_primera_mensualidad, formData.fecha_inicio, formData.dia_pago]);

  // Calcular desglose de pagos para el formulario de edición
  const calcularDesglosePagosEdit = () => {
    const precioFinal = parseFloat(editFormData.precio_final || 0);
    const enganche = parseFloat(editFormData.enganche || 0);
    const montoFinanciado = precioFinal - enganche;
    
    let pagos = [];
    
    // IMPORTANTE: Usar fecha_primera_mensualidad para calcular las fechas de los pagos
    const fechaPrimeraMensualidad = editFormData.fecha_primera_mensualidad || editFormData.fecha_inicio;
    
    if (editFormData.modo_calculo === 'mensualidad_fija') {
      const mensualidad = parseFloat(editFormData.monto_mensualidad || 0);
      if (mensualidad > 0 && montoFinanciado > 0) {
        const numPagos = Math.ceil(montoFinanciado / mensualidad);
        const fechaBase = new Date(fechaPrimeraMensualidad || new Date());
        const dia = parseInt(editFormData.dia_pago) || fechaBase.getDate();
        
        for (let i = 0; i < numPagos; i++) {
          const montoRestante = montoFinanciado - (i * mensualidad);
          const montoPago = Math.min(mensualidad, montoRestante);
          // CAMBIO: El primer pago es en el MISMO mes de fecha_primera_mensualidad (sin +1)
          const fechaPago = new Date(fechaBase.getFullYear(), fechaBase.getMonth() + i, dia);
          
          pagos.push({
            numero: i + 1,
            monto: Math.round(montoPago * 100) / 100,
            fecha: fechaPago.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }),
            esUltimo: i === numPagos - 1 && montoPago < mensualidad
          });
        }
      }
    } else {
      // Meses fijos
      const numMeses = parseInt(editFormData.numero_meses || 0);
      if (numMeses > 0 && montoFinanciado > 0) {
        const mensualidad = montoFinanciado / numMeses;
        const fechaBase = new Date(fechaPrimeraMensualidad || new Date());
        const dia = parseInt(editFormData.dia_pago) || fechaBase.getDate();
        
        for (let i = 0; i < numMeses; i++) {
          // CAMBIO: El primer pago es en el MISMO mes de fecha_primera_mensualidad (sin +1)
          const fechaPago = new Date(fechaBase.getFullYear(), fechaBase.getMonth() + i, dia);
          pagos.push({
            numero: i + 1,
            monto: Math.round(mensualidad * 100) / 100,
            fecha: fechaPago.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }),
            esUltimo: false
          });
        }
      }
    }
    
    setDesglosePagosEdit(pagos);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        lotes_ids: formData.lotes_ids,
        cliente_id: formData.cliente_id,
        tipo_pago: formData.tipo_pago,
        metodo_pago: formData.metodo_pago,  // efectivo, transferencia, tarjeta, cheque
        moneda: formData.moneda,  // USD o MXN
        tipo_cambio: formData.moneda === 'MXN' && formData.tipo_cambio ? parseFloat(formData.tipo_cambio) : null,
        precio_final: formData.precio_final ? parseFloat(formData.precio_final) : null,
        fecha_inicio: formData.fecha_inicio,
        notas: formData.notas || null
      };
      
      if (formData.tipo_pago === 'financiado') {
        payload.enganche = parseFloat(formData.enganche || '0');
        payload.fecha_enganche = formData.fecha_enganche || null;  // Fecha del enganche
        payload.metodo_pago_enganche = formData.metodo_pago_enganche || null;
        payload.fecha_primera_mensualidad = formData.fecha_primera_mensualidad || formData.fecha_inicio;  // Fecha primera mensualidad
        payload.modo_calculo = formData.modo_calculo;
        payload.modo_fecha = 'dia_fijo'; // Siempre día fijo ahora
        payload.interes_mensual = parseFloat(formData.interes_mensual || '0');
        payload.interes_atraso = parseFloat(formData.interes_atraso || '0');
        payload.interes_venta = parseFloat(formData.interes_venta || '0');
        
        if (formData.modo_calculo === 'meses_fijos') {
          payload.numero_meses = parseInt(formData.numero_meses);
          // Si hay interés y mensualidad calculada, enviarla también
          if (parseFloat(formData.interes_venta || 0) > 0 && mensualidadCalculada) {
            payload.monto_mensualidad = mensualidadCalculada;
          }
        } else if (formData.modo_calculo === 'mensualidad_fija') {
          payload.monto_mensualidad = parseFloat(formData.monto_mensualidad);
          // Si hay pagos personalizados con montos editados, enviarlos
          if (pagosPersonalizados.length > 0) {
            payload.pagos_personalizados = pagosPersonalizados.map(p => ({
              numero: p.numero,
              monto: p.monto,
              fecha: p.fecha
            }));
          }
        }
        
        payload.dia_pago = formData.dia_pago ? parseInt(formData.dia_pago) : null;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/tratos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      const responseText = await response.text();
      
      if (!response.ok) {
        let errorMessage = 'Error al crear trato';
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = responseText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      toast.success('Trato creado correctamente');
      setDialogOpen(false);
      setFormFraccFilter('todos'); // Resetear filtro
      setPrecioOriginalLotes(0);
      setMonedaOriginalLotes('USD');
      setTablaAmortizacion(null);
      setMostrarTablaAmortizacion(false);
      setFormData({
        lotes_ids: [],
        cliente_id: '',
        tipo_pago: 'financiado',
        metodo_pago: 'efectivo',
        moneda: 'USD',
        tipo_cambio: '',
        precio_final: '',
        enganche: '',
        modo_calculo: 'mensualidad_fija',
        numero_meses: '',
        monto_mensualidad: '',
        fecha_inicio: new Date().toISOString().split('T')[0],
        fecha_primera_mensualidad: new Date().toISOString().split('T')[0],
        modo_fecha: 'dia_fijo',
        dia_pago: '',
        fecha_unica_pago: '',
        interes_mensual: '0',
        interes_atraso: '0',
        interes_venta: '0',
        notas: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.trato) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/tratos/${deleteDialog.trato.trato_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al eliminar');
      }
      
      toast.success('Trato movido a papelera');
      setDeleteDialog({ open: false, trato: null });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Estado para guardar la fecha original al editar
  const [fechaInicioOriginal, setFechaInicioOriginal] = useState('');
  
  const handleOpenEdit = (trato) => {
    setEditingTrato(trato);
    let diaPago = trato.dia_pago?.toString() || '';
    if (!diaPago && trato.fecha_inicio) {
      const fechaInicio = new Date(trato.fecha_inicio);
      diaPago = fechaInicio.getDate().toString();
    }
    if (!diaPago) diaPago = '15';
    
    const fechaInicioFormatted = trato.fecha_inicio ? new Date(trato.fecha_inicio).toISOString().split('T')[0] : '';
    setFechaInicioOriginal(fechaInicioFormatted); // Guardar fecha original
    
    // Obtener lote_id: puede ser lote_id directo o el primer elemento de lotes_ids
    const loteId = trato.lote_id || (trato.lotes_ids && trato.lotes_ids.length > 0 ? trato.lotes_ids[0] : '');
    
    setEditFormData({
      lote_id: loteId,
      cliente_id: trato.cliente_id || '',
      tipo_pago: trato.tipo_pago || 'financiado',
      metodo_pago: trato.metodo_pago || 'efectivo',
      moneda: trato.moneda || 'USD',
      tipo_cambio: trato.tipo_cambio?.toString() || '',
      precio_final: trato.precio_final?.toString() || '',
      enganche: trato.enganche?.toString() || '',
      fecha_enganche: trato.fecha_enganche ? new Date(trato.fecha_enganche).toISOString().split('T')[0] : '',
      metodo_pago_enganche: trato.metodo_pago_enganche || '',
      modo_calculo: trato.modo_calculo || 'meses_fijos',
      numero_meses: trato.numero_meses?.toString() || '',
      monto_mensualidad: trato.monto_mensualidad?.toString() || '',
      fecha_inicio: fechaInicioFormatted,
      fecha_primera_mensualidad: trato.fecha_primera_mensualidad ? new Date(trato.fecha_primera_mensualidad).toISOString().split('T')[0] : '',
      modo_fecha: 'dia_fijo',  // Siempre día fijo
      dia_pago: diaPago,
      fecha_unica_pago: '',
      interes_mensual: trato.interes_mensual?.toString() || '0',
      interes_atraso: trato.interes_atraso?.toString() || '0',
      interes_venta: trato.interes_venta?.toString() || '0',
      notas: trato.notas || ''
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingTrato) return;
    
    if (editFormData.modo_fecha === 'dia_fijo' && !editFormData.dia_pago) {
      toast.error('Por favor selecciona un día de pago');
      return;
    }
    
    // Detectar si la fecha de inicio cambió
    const fechaCambio = editFormData.fecha_inicio !== fechaInicioOriginal;
    console.log('DEBUG - fechaInicioOriginal:', fechaInicioOriginal);
    console.log('DEBUG - editFormData.fecha_inicio:', editFormData.fecha_inicio);
    console.log('DEBUG - fechaCambio:', fechaCambio);
    
    try {
      const payload = {
        precio_final: parseFloat(editFormData.precio_final),
        enganche: parseFloat(editFormData.enganche),
        fecha_enganche: editFormData.fecha_enganche || null,
        fecha_primera_mensualidad: editFormData.fecha_primera_mensualidad || editFormData.fecha_inicio || null,
        modo_calculo: editFormData.modo_calculo,
        fecha_inicio: editFormData.fecha_inicio || null,
        modo_fecha: editFormData.modo_fecha,
        interes_mensual: parseFloat(editFormData.interes_mensual || '0'),
        interes_atraso: parseFloat(editFormData.interes_atraso || '0'),
        interes_venta: parseFloat(editFormData.interes_venta || '0'),
        notas: editFormData.notas || null,
        lote_id: editFormData.lote_id || null,  // Incluir lote_id en la actualización
        // SIEMPRE reiniciar todos los pagos cuando se edita el trato para recalcular fechas
        regenerar_pagos: false,
        reiniciar_todos_pagos: true  // Siempre reiniciar para garantizar que las fechas se recalculen
      };
      
      console.log('DEBUG - Payload enviado:', JSON.stringify(payload));
      
      if (editFormData.modo_calculo === 'meses_fijos') {
        payload.numero_meses = parseInt(editFormData.numero_meses);
      } else if (editFormData.modo_calculo === 'mensualidad_fija') {
        payload.monto_mensualidad = parseFloat(editFormData.monto_mensualidad);
      }
      
      // Siempre usar día fijo
      payload.dia_pago = parseInt(editFormData.dia_pago);
      payload.modo_fecha = 'dia_fijo';
      
      const response = await fetch(`${BACKEND_URL}/api/tratos/${editingTrato.trato_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      const responseText = await response.text();
      
      if (!response.ok) {
        let errorMessage = 'Error al editar trato';
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = responseText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      // Siempre mostramos que los pagos fueron reiniciados
      toast.success('Trato actualizado. Todos los pagos han sido reiniciados con las nuevas fechas.');
      setEditDialogOpen(false);
      setEditingTrato(null);
      setFechaInicioOriginal(''); // Limpiar la fecha original
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Filtrar tratos por fraccionamiento seleccionado y búsqueda
  const tratosFiltrados = tratos.filter(trato => {
    // Filtro por fraccionamiento
    let matchFracc = true;
    if (selectedFraccId !== 'todos') {
      const lotesIds = trato.lotes_ids || (trato.lote_id ? [trato.lote_id] : []);
      matchFracc = lotesIds.some(loteId => {
        const lote = lotes.find(l => l.lote_id === loteId);
        return lote?.fraccionamiento_id === selectedFraccId;
      });
    }
    
    // Filtro por búsqueda
    let matchSearch = true;
    if (searchTerm) {
      const cliente = clientes.find(c => c.cliente_id === trato.cliente_id);
      const clienteNombre = cliente?.nombre_completo?.toLowerCase() || '';
      const lotesIds = trato.lotes_ids || (trato.lote_id ? [trato.lote_id] : []);
      const lotesDelTrato = lotesIds.map(id => lotes.find(l => l.lote_id === id)).filter(Boolean);
      const lotesNumeros = lotesDelTrato.map(l => l.numero).join(' ');
      const manzanas = lotesDelTrato.map(l => l.manzana).join(' ');
      
      const searchLower = searchTerm.toLowerCase();
      matchSearch = clienteNombre.includes(searchLower) || 
                    lotesNumeros.includes(searchTerm) ||
                    manzanas.toLowerCase().includes(searchLower);
    }
    
    return matchFracc && matchSearch;
  });

  return (
    <div data-testid="tratos-page" className="min-h-screen bg-zinc-950 p-8">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-10">
        <div>
          <h1 className="text-4xl font-bold text-white tracking-tight">Tratos de Venta</h1>
          <p className="text-zinc-400 mt-2">Administra los contratos de venta y financiamiento</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Búsqueda */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              data-testid="search-tratos"
              placeholder="Buscar cliente, lote..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-[200px] bg-zinc-900 border-zinc-700 text-white"
            />
          </div>
          
          {/* Filtro por Fraccionamiento */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-500" />
            <Select value={selectedFraccId} onValueChange={handleFraccFilter}>
              <SelectTrigger className="w-[200px] bg-zinc-900 border-zinc-700 text-white">
                <SelectValue placeholder="Filtrar por..." />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700">
                <SelectItem value="todos" className="text-white hover:bg-zinc-800">Todos los fraccionamientos</SelectItem>
                {fraccionamientos.map((f) => (
                  <SelectItem key={f.fraccionamiento_id} value={f.fraccionamiento_id} className="text-white hover:bg-zinc-800">
                    {f.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {canWrite ? (
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setBusquedaLoteCrear(''); setBusquedaClienteCrear(''); } }}>
              <DialogTrigger asChild>
                <Button data-testid="create-trato-button" className="bg-amber-500 hover:bg-amber-400 text-black font-bold">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Trato
                </Button>
              </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto bg-zinc-900 border-zinc-800 text-white">
            <DialogHeader>
              <DialogTitle className="text-white">Crear Trato de Venta</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Selector de múltiples lotes */}
              <div>
                <Label className="text-zinc-300">Lotes a incluir en el trato</Label>
                
                {/* Filtro por fraccionamiento */}
                <div className="mt-2 mb-3">
                  <Select 
                    value={formFraccFilter} 
                    onValueChange={(value) => {
                      setFormFraccFilter(value);
                      // 🔥 ESTANDARIZADO: Guardar con misma clave
                      localStorage.setItem('filtro_fraccionamiento', value);
                      // Limpiar lotes seleccionados y resetear moneda cuando cambias de fraccionamiento
                      setPrecioOriginalLotes(0);
                      setMonedaOriginalLotes('USD');
                      setFormData(prev => ({
                        ...prev,
                        lotes_ids: [],
                        precio_final: '',
                        moneda: 'USD',
                        tipo_cambio: ''
                      }));
                    }}
                  >
                    <SelectTrigger className="bg-zinc-700 border-zinc-600 text-white">
                      <SelectValue placeholder="Filtrar por fraccionamiento..." />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="todos" className="text-white hover:bg-zinc-700">Todos los fraccionamientos</SelectItem>
                      {fraccionamientos.map((f) => (
                        <SelectItem key={f.fraccionamiento_id} value={f.fraccionamiento_id} className="text-white hover:bg-zinc-700">
                          {f.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Buscador de lotes */}
                <div>
                  <Input
                    type="text"
                    placeholder="Buscar por número de lote o manzana..."
                    value={busquedaLoteCrear}
                    onChange={(e) => setBusquedaLoteCrear(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                  />
                </div>
                
                <div className="border border-zinc-700 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2 bg-zinc-800">
                  {(() => {
                    const busqueda = busquedaLoteCrear.toLowerCase().trim();
                    const lotesFiltrados = lotes
                      .filter(l => l.estado === 'disponible' || l.estado === 'apartado')
                      .filter(l => formFraccFilter === 'todos' || l.fraccionamiento_id === formFraccFilter)
                      .filter(l => {
                        if (!busqueda) return true;
                        const loteNum = String(l.numero || '').toLowerCase();
                        const manzana = String(l.manzana || '').toLowerCase();
                        return loteNum.includes(busqueda) || manzana.includes(busqueda) || 
                               `lote ${loteNum}`.includes(busqueda) || `manzana ${manzana}`.includes(busqueda) ||
                               `mz ${manzana}`.includes(busqueda);
                      })
                      .sort((a, b) => {
                        // Ordenar por manzana y luego por número
                        const mzA = String(a.manzana || '');
                        const mzB = String(b.manzana || '');
                        if (mzA !== mzB) return mzA.localeCompare(mzB, undefined, {numeric: true});
                        return parseInt(a.numero || 0) - parseInt(b.numero || 0);
                      });
                    
                    if (lotesFiltrados.length === 0) {
                      return <p className="text-sm text-zinc-500">No hay lotes disponibles {busqueda ? 'que coincidan con la búsqueda' : 'en este fraccionamiento'}</p>;
                    }
                    
                    return lotesFiltrados.map((lote) => {
                      const fracc = fraccionamientos.find(f => f.fraccionamiento_id === lote.fraccionamiento_id);
                      const isSelected = formData.lotes_ids.includes(lote.lote_id);
                      const loteMoneda = lote.moneda || 'USD';
                      const label = [
                        `Lote ${lote.numero}`,
                        lote.manzana ? `Mzna. ${lote.manzana}` : null,
                        fracc?.nombre
                      ].filter(Boolean).join(' · ');
                      return (
                        <div 
                          key={lote.lote_id}
                          className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-zinc-700 ${isSelected ? 'bg-cyan-500/20 border border-cyan-500/50' : ''}`}
                          onClick={() => {
                            const newIds = isSelected 
                              ? formData.lotes_ids.filter(id => id !== lote.lote_id)
                              : [...formData.lotes_ids, lote.lote_id];
                            const lotesSeleccionados = lotes.filter(l => newIds.includes(l.lote_id));
                            const precioTotal = lotesSeleccionados.reduce((sum, l) => sum + (l.precio || l.precio_actual || l.precio_base || 0), 0);
                            // Determinar la moneda basándose en el primer lote seleccionado
                            const monedaLotes = lotesSeleccionados.length > 0 ? (lotesSeleccionados[0].moneda || 'USD') : 'USD';
                            // Guardar precio y moneda original de los lotes
                            setPrecioOriginalLotes(precioTotal);
                            setMonedaOriginalLotes(monedaLotes);
                            setFormData({
                              ...formData, 
                              lotes_ids: newIds,
                              precio_final: precioTotal.toString(),
                              moneda: monedaLotes,
                              tipo_cambio: ''  // Resetear tipo de cambio
                            });
                          }}
                        >
                          <input type="checkbox" checked={isSelected} onChange={() => {}} className="w-4 h-4 accent-cyan-500" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-white">{label}</p>
                            <p className="text-xs text-zinc-400">{loteMoneda} ${(lote.precio || lote.precio_actual || 0).toLocaleString('es-MX')}</p>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
                {formData.lotes_ids.length > 0 && (
                  <p className="text-xs text-amber-500 mt-1">
                    ✓ {formData.lotes_ids.length} lote(s) seleccionado(s)
                  </p>
                )}
              </div>
              <div>
                <Label className="text-zinc-300">Cliente</Label>
                {/* Buscador de clientes */}
                <Input
                  type="text"
                  placeholder="Buscar cliente por nombre..."
                  value={busquedaClienteCrear}
                  onChange={(e) => setBusquedaClienteCrear(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 mb-2"
                />
                <Select value={formData.cliente_id} onValueChange={(value) => setFormData({...formData, cliente_id: value})} required>
                  <SelectTrigger data-testid="select-cliente" className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue placeholder="Selecciona un cliente" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                    {clientes
                      .filter(c => {
                        if (!busquedaClienteCrear.trim()) return true;
                        const busqueda = busquedaClienteCrear.toLowerCase();
                        return c.nombre_completo?.toLowerCase().includes(busqueda);
                      })
                      .sort((a, b) => (a.nombre_completo || '').localeCompare(b.nombre_completo || ''))
                      .map((cliente) => (
                        <SelectItem key={cliente.cliente_id} value={cliente.cliente_id} className="text-white hover:bg-zinc-700">
                          {cliente.nombre_completo}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Tipo de pago */}
              <div className="bg-zinc-800 p-4 rounded-lg border border-zinc-700">
                <Label className="text-sm font-bold mb-3 block text-amber-500">Tipo de Pago</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div 
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${formData.tipo_pago === 'efectivo' ? 'bg-emerald-500/20 border-emerald-500' : 'bg-zinc-700 border-zinc-600 hover:border-zinc-500'}`}
                    onClick={() => setFormData({...formData, tipo_pago: 'efectivo'})}
                  >
                    <div className="flex items-center gap-2">
                      <input type="radio" checked={formData.tipo_pago === 'efectivo'} onChange={() => {}} className="accent-emerald-500" />
                      <span className="font-bold text-emerald-400">EFECTIVO</span>
                    </div>
                    <p className="text-xs text-zinc-400 mt-1">Pago único del total</p>
                  </div>
                  <div 
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${formData.tipo_pago === 'financiado' ? 'bg-amber-500/20 border-amber-500' : 'bg-zinc-700 border-zinc-600 hover:border-zinc-500'}`}
                    onClick={() => setFormData({...formData, tipo_pago: 'financiado'})}
                  >
                    <div className="flex items-center gap-2">
                      <input type="radio" checked={formData.tipo_pago === 'financiado'} onChange={() => {}} className="accent-amber-500" />
                      <span className="font-bold text-amber-400">FINANCIADO</span>
                    </div>
                    <p className="text-xs text-zinc-400 mt-1">Enganche + mensualidades</p>
                  </div>
                </div>
              </div>
              
              {/* Moneda de la venta */}
              <div className="bg-zinc-800 p-4 rounded-lg border border-zinc-700">
                <Label className="text-sm font-bold mb-3 block text-amber-500">Moneda de la Venta</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div 
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${formData.moneda === 'USD' ? 'bg-emerald-500/20 border-emerald-500' : 'bg-zinc-700 border-zinc-600 hover:border-zinc-500'}`}
                    onClick={() => {
                      // Cambiar a USD
                      if (monedaOriginalLotes === 'USD') {
                        // El lote ya está en USD, usar precio original
                        setFormData({
                          ...formData, 
                          moneda: 'USD',
                          tipo_cambio: '',
                          precio_final: precioOriginalLotes > 0 ? precioOriginalLotes.toString() : formData.precio_final
                        });
                      } else {
                        // El lote está en MXN, convertir a USD
                        const precioMXN = parseFloat(formData.precio_final) || precioOriginalLotes;
                        const precioUSD = tipoCambioActual > 0 ? Math.round(precioMXN / tipoCambioActual * 100) / 100 : precioMXN;
                        setFormData({
                          ...formData, 
                          moneda: 'USD',
                          tipo_cambio: tipoCambioActual.toString(),
                          precio_final: precioUSD.toString()
                        });
                      }
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <input type="radio" checked={formData.moneda === 'USD'} onChange={() => {}} className="accent-emerald-500" />
                      <span className="font-bold text-emerald-400">🇺🇸 USD</span>
                    </div>
                    <p className="text-xs text-zinc-400 mt-1">Dólares americanos</p>
                  </div>
                  <div 
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${formData.moneda === 'MXN' ? 'bg-amber-500/20 border-amber-500' : 'bg-zinc-700 border-zinc-600 hover:border-zinc-500'}`}
                    onClick={() => {
                      // Cambiar a MXN
                      if (monedaOriginalLotes === 'MXN') {
                        // El lote ya está en MXN, usar precio original sin conversión
                        setFormData({
                          ...formData, 
                          moneda: 'MXN',
                          tipo_cambio: '',
                          precio_final: precioOriginalLotes > 0 ? precioOriginalLotes.toString() : formData.precio_final
                        });
                      } else {
                        // El lote está en USD, convertir a MXN
                        const precioUSD = parseFloat(formData.precio_final) || precioOriginalLotes;
                        const precioMXN = Math.round(precioUSD * tipoCambioActual * 100) / 100;
                        setFormData({
                          ...formData, 
                          moneda: 'MXN',
                          tipo_cambio: tipoCambioActual.toString(),
                          precio_final: precioMXN > 0 ? precioMXN.toString() : ''
                        });
                      }
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <input type="radio" checked={formData.moneda === 'MXN'} onChange={() => {}} className="accent-amber-500" />
                      <span className="font-bold text-amber-400">🇲🇽 MXN</span>
                    </div>
                    <p className="text-xs text-zinc-400 mt-1">Pesos mexicanos</p>
                  </div>
                </div>
                
                {/* Tipo de cambio - solo visible cuando la moneda del trato es diferente a la del lote */}
                {formData.tipo_cambio && (
                  <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-amber-400 font-medium text-sm">Tipo de Cambio (MXN/USD)</Label>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={actualizarTipoCambio}
                        disabled={loadingTipoCambio}
                        className="h-7 text-amber-400 hover:text-amber-300"
                      >
                        <RefreshCw className={`w-4 h-4 mr-1 ${loadingTipoCambio ? 'animate-spin' : ''}`} />
                        Actualizar
                      </Button>
                    </div>
                    <Input 
                      type="number" 
                      step="0.01" 
                      value={formData.tipo_cambio} 
                      onChange={(e) => {
                        const nuevoTC = parseFloat(e.target.value) || 0;
                        // Recalcular el precio basado en la conversión
                        let nuevoPrecio = formData.precio_final;
                        if (monedaOriginalLotes === 'USD' && formData.moneda === 'MXN') {
                          nuevoPrecio = Math.round(precioOriginalLotes * nuevoTC * 100) / 100;
                        } else if (monedaOriginalLotes === 'MXN' && formData.moneda === 'USD') {
                          nuevoPrecio = nuevoTC > 0 ? Math.round(precioOriginalLotes / nuevoTC * 100) / 100 : precioOriginalLotes;
                        }
                        setFormData({
                          ...formData, 
                          tipo_cambio: e.target.value,
                          precio_final: nuevoPrecio.toString()
                        });
                      }} 
                      className="bg-zinc-800 border-amber-500/30 text-white text-lg font-mono" 
                    />
                    <p className="text-xs text-zinc-400 mt-2">
                      Precio original del lote: <span className="text-emerald-400 font-mono font-bold">{monedaOriginalLotes} ${precioOriginalLotes.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                    </p>
                  </div>
                )}
              </div>
              
              <div>
                <Label className="text-zinc-300">
                  Precio Final ({formData.moneda === 'USD' ? '🇺🇸 USD' : '🇲🇽 MXN'})
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.precio_final}
                  onChange={(e) => setFormData({...formData, precio_final: e.target.value})}
                  required
                  className="bg-zinc-800 border-zinc-700 text-white text-lg font-mono"
                  placeholder={formData.moneda === 'USD' ? 'Ej: 15000' : 'Ej: 250000'}
                />
                {formData.moneda === 'MXN' && formData.tipo_cambio && formData.precio_final && (
                  <p className="text-xs text-amber-400 mt-1">
                    ≈ USD ${(parseFloat(formData.precio_final) / parseFloat(formData.tipo_cambio)).toLocaleString('es-MX', { minimumFractionDigits: 2 })} al TC de ${formData.tipo_cambio}
                  </p>
                )}
              </div>
              
              <div>
                <Label className="text-zinc-300">📅 Fecha del Trato</Label>
                <Input
                  type="date"
                  value={formData.fecha_inicio}
                  onChange={(e) => setFormData({...formData, fecha_inicio: e.target.value})}
                  required
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
                <p className="text-xs text-zinc-500 mt-1">Fecha en que se firmó/realizó el trato</p>
              </div>
              
              {formData.tipo_pago === 'financiado' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-300">
                        Enganche ({formData.moneda === 'USD' ? '🇺🇸 USD' : '🇲🇽 MXN'})
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.enganche}
                        onChange={(e) => setFormData({...formData, enganche: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="0 si no hay enganche"
                      />
                    </div>
                    
                    {/* Solo mostrar fecha de enganche si hay monto de enganche */}
                    {parseFloat(formData.enganche) > 0 && (
                      <div>
                        <Label className="text-zinc-300">📅 Fecha del Enganche</Label>
                        <Input
                          type="date"
                          value={formData.fecha_enganche}
                          onChange={(e) => setFormData({...formData, fecha_enganche: e.target.value})}
                          className="bg-zinc-800 border-zinc-700 text-white"
                        />
                        <p className="text-xs text-zinc-500 mt-1">Fecha en que se recibe el enganche</p>
                      </div>
                    )}
                    
                    {/* Método de pago del enganche */}
                    {parseFloat(formData.enganche) > 0 && (
                      <div>
                        <Label className="text-zinc-300">Método de Pago del Enganche</Label>
                        <select
                          value={formData.metodo_pago_enganche}
                          onChange={(e) => setFormData({...formData, metodo_pago_enganche: e.target.value})}
                          className="w-full h-10 px-3 rounded-md bg-zinc-800 border border-zinc-700 text-white text-sm"
                          data-testid="metodo-pago-enganche"
                        >
                          <option value="">Seleccionar...</option>
                          <option value="efectivo">Efectivo</option>
                          <option value="transferencia">Transferencia</option>
                          <option value="cheque">Cheque</option>
                          <option value="tarjeta">Tarjeta</option>
                        </select>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <Label className="text-zinc-300">📅 Fecha Primera Mensualidad</Label>
                    <Input
                      type="date"
                      value={formData.fecha_primera_mensualidad}
                      onChange={(e) => setFormData({...formData, fecha_primera_mensualidad: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                    <p className="text-xs text-zinc-500 mt-1">Fecha de inicio del plan de pagos</p>
                  </div>
              
                  {/* SECCIÓN UNIFICADA DE FINANCIAMIENTO */}
                  <div className="bg-zinc-800 p-4 rounded-lg border border-zinc-700">
                    <Label className="text-sm font-semibold mb-3 block text-amber-500">💰 Configuración de Pagos</Label>
                    
                    {/* Modo de Cálculo */}
                    <div className="mb-4 p-3 bg-zinc-900 rounded border border-zinc-700">
                      <Label className="text-xs text-zinc-400 mb-2 block">Modo de Cálculo</Label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            value="meses_fijos"
                            checked={formData.modo_calculo === 'meses_fijos'}
                            onChange={(e) => setFormData({...formData, modo_calculo: e.target.value})}
                            className="accent-amber-500"
                          />
                          <span className="text-sm text-white">Meses fijos</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            value="mensualidad_fija"
                            checked={formData.modo_calculo === 'mensualidad_fija'}
                            onChange={(e) => setFormData({...formData, modo_calculo: e.target.value})}
                            className="accent-amber-500"
                          />
                          <span className="text-sm text-white">Mensualidad fija</span>
                        </label>
                      </div>
                    </div>
                    
                    {/* Campos según modo de cálculo */}
                    {formData.modo_calculo === 'meses_fijos' ? (
                      <div className="mb-4">
                        <Label className="text-zinc-300">Plazo (meses)</Label>
                        <Input
                          type="number"
                          min="1"
                          value={formData.numero_meses}
                          onChange={(e) => setFormData({...formData, numero_meses: e.target.value})}
                          placeholder="Ej: 12"
                          className="bg-zinc-700 border-zinc-600 text-white"
                        />
                        <p className="text-xs text-zinc-500 mt-1">
                          {parseFloat(formData.interes_venta || 0) > 0 
                            ? 'El sistema calculará la mensualidad con interés compuesto' 
                            : 'El sistema calculará la mensualidad dividiendo el monto'}
                        </p>
                        
                        {/* Mostrar mensualidad calculada cuando hay interés */}
                        {parseFloat(formData.interes_venta || 0) > 0 && mensualidadCalculada && (
                          <div className="mt-2 p-2 bg-emerald-500/10 border border-emerald-500/30 rounded">
                            <p className="text-sm text-emerald-400 font-semibold">
                              Mensualidad calculada: ${mensualidadCalculada.toLocaleString('es-MX', {minimumFractionDigits: 2})} / mes
                            </p>
                          </div>
                        )}
                        
                        {/* Indicador de cálculo para meses fijos */}
                        {loadingMensualidad && (
                          <div className="mt-2 flex items-center">
                            <RefreshCw className="w-3 h-3 mr-2 animate-spin text-amber-400" />
                            <span className="text-xs text-zinc-400">Calculando mensualidad...</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="mb-4">
                        <Label className="text-zinc-300">Pago Mensual Fijo ({formData.moneda})</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="1"
                          value={formData.monto_mensualidad}
                          onChange={(e) => setFormData({...formData, monto_mensualidad: e.target.value})}
                          placeholder="Ej: 5000"
                          className="bg-zinc-700 border-zinc-600 text-white"
                        />
                        <p className="text-xs text-zinc-500 mt-1">Monto fijo que paga el cliente cada mes</p>
                      </div>
                    )}
                    
                    {/* Interés de Venta */}
                    <div className="mb-4">
                      <Label className="text-zinc-300">Interés Mensual (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.interes_venta}
                        onChange={(e) => setFormData({...formData, interes_venta: e.target.value})}
                        className="bg-zinc-700 border-zinc-600 text-white"
                        placeholder="0 = sin interés"
                      />
                      <p className="text-xs text-zinc-500 mt-1">Se aplica sobre el saldo pendiente cada mes (interés compuesto)</p>
                    </div>
                    
                    {/* Indicador de cálculo */}
                    {loadingAmortizacion && (
                      <div className="flex items-center justify-center p-3 bg-zinc-900 rounded border border-zinc-700">
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin text-amber-400" />
                        <span className="text-zinc-400 text-sm">Calculando amortización...</span>
                      </div>
                    )}
                    
                    {/* RESUMEN DE AMORTIZACIÓN - Cuando hay interés (ambos modos) */}
                    {parseFloat(formData.interes_venta || 0) > 0 && tablaAmortizacion && mostrarTablaAmortizacion && (
                      <div className="mt-4 bg-zinc-900 rounded-lg border border-emerald-500/30">
                        {/* Resumen Financiero */}
                        <div className="p-4">
                          <div className="flex justify-between items-center mb-3">
                            <h4 className="text-emerald-400 font-bold">📋 Resumen del Financiamiento</h4>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setModalAmortizacionAbierto(true)}
                              className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                            >
                              <Maximize2 className="w-4 h-4 mr-1" />
                              Ver Desglose Completo
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            <div className="text-center p-2 bg-zinc-800 rounded">
                              <p className="text-xs text-zinc-500">Meses</p>
                              <p className="text-xl font-bold text-white">{tablaAmortizacion.total_meses}</p>
                            </div>
                            <div className="text-center p-2 bg-zinc-800 rounded">
                              <p className="text-xs text-zinc-500">Mensualidad</p>
                              <p className="text-lg font-bold text-cyan-400">
                                ${(formData.modo_calculo === 'meses_fijos' ? mensualidadCalculada : parseFloat(formData.monto_mensualidad || 0))?.toLocaleString('es-MX', {minimumFractionDigits: 2})}
                              </p>
                            </div>
                            <div className="text-center p-2 bg-zinc-800 rounded">
                              <p className="text-xs text-zinc-500">Total Interés</p>
                              <p className="text-lg font-bold text-amber-400">${tablaAmortizacion.total_interes?.toLocaleString('es-MX', {minimumFractionDigits: 2})}</p>
                            </div>
                            <div className="text-center p-2 bg-zinc-800 rounded">
                              <p className="text-xs text-zinc-500">Total a Pagar</p>
                              <p className="text-lg font-bold text-emerald-400">${tablaAmortizacion.total_pagado?.toLocaleString('es-MX', {minimumFractionDigits: 2})}</p>
                            </div>
                            <div className="text-center p-2 bg-zinc-800 rounded">
                              <p className="text-xs text-zinc-500">Liquidación</p>
                              <p className="text-sm font-bold text-cyan-400">{formatDateSafe(tablaAmortizacion.fecha_liquidacion)}</p>
                            </div>
                          </div>
                          <p className="text-xs text-zinc-500 mt-3 text-center">
                            Haz clic en "Ver Desglose Completo" para ver la tabla detallada de pagos
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Mensaje cuando hay interés pero falta información (mensualidad fija) */}
                    {parseFloat(formData.interes_venta || 0) > 0 && formData.modo_calculo === 'mensualidad_fija' && !tablaAmortizacion && !loadingAmortizacion && parseFloat(formData.monto_mensualidad || 0) > 0 && (
                      <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                        {(() => {
                          const saldo = parseFloat(formData.precio_final || 0) - parseFloat(formData.enganche || 0);
                          const tasaInteres = parseFloat(formData.interes_venta || 0);
                          const interesPrimerMes = saldo * (tasaInteres / 100);
                          const pagoActual = parseFloat(formData.monto_mensualidad || 0);
                          
                          if (saldo <= 0) {
                            return <p className="text-sm text-red-400">⚠️ Ingresa un precio final válido</p>;
                          }
                          if (pagoActual <= interesPrimerMes) {
                            return (
                              <div>
                                <p className="text-sm text-red-400 font-semibold">⚠️ El pago mensual no cubre el interés</p>
                                <p className="text-xs text-zinc-400 mt-1">
                                  Interés primer mes: ${interesPrimerMes.toLocaleString('es-MX', {minimumFractionDigits: 2})} | 
                                  Pago mínimo: ${(interesPrimerMes + 100).toLocaleString('es-MX', {minimumFractionDigits: 2})}
                                </p>
                              </div>
                            );
                          }
                          return <p className="text-sm text-zinc-400">Completa fecha de primera mensualidad</p>;
                        })()}
                      </div>
                    )}
                    
                    {/* MODO SIN INTERÉS - Desglose simple */}
                    {parseFloat(formData.interes_venta || 0) === 0 && parseFloat(formData.precio_final || 0) > 0 && (
                      <div className="mt-4">
                        {(() => {
                          const saldo = parseFloat(formData.precio_final || 0) - parseFloat(formData.enganche || 0);
                          
                          if (formData.modo_calculo === 'meses_fijos' && formData.numero_meses) {
                            const numMeses = parseInt(formData.numero_meses);
                            const mensualidad = saldo / numMeses;
                            return (
                              <div className="p-3 bg-zinc-900 rounded border border-zinc-700">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-zinc-400">Sin interés</span>
                                  <span className="text-lg font-bold text-emerald-400">{numMeses} pagos de ${mensualidad.toLocaleString('es-MX', {minimumFractionDigits: 2})}</span>
                                </div>
                              </div>
                            );
                          } else if (formData.modo_calculo === 'mensualidad_fija' && formData.monto_mensualidad) {
                            const mensualidad = parseFloat(formData.monto_mensualidad);
                            const numPagos = Math.ceil(saldo / mensualidad);
                            const ultimoPago = saldo - (Math.floor(saldo / mensualidad) * mensualidad);
                            return (
                              <div className="p-3 bg-zinc-900 rounded border border-zinc-700">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="text-sm text-zinc-400">Sin interés (división simple)</span>
                                  <span className="text-lg font-bold text-emerald-400">{numPagos} pagos</span>
                                </div>
                                <p className="text-xs text-zinc-500 mb-3">
                                  {ultimoPago > 0 && ultimoPago !== mensualidad
                                    ? `${numPagos - 1} pagos de $${mensualidad.toLocaleString('es-MX')} + 1 pago de ajuste de $${ultimoPago.toLocaleString('es-MX', {minimumFractionDigits: 2})}`
                                    : `${numPagos} pagos de $${mensualidad.toLocaleString('es-MX')}`
                                  }
                                </p>
                                
                                {/* Botón para ver desglose de pagos */}
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => {
                                    actualizarPreviaPagos();
                                    setMostrarPreviaPagos(!mostrarPreviaPagos);
                                  }}
                                  className="w-full border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                                >
                                  {mostrarPreviaPagos ? 'Ocultar' : 'Ver'} desglose de pagos ({numPagos} pagos)
                                </Button>
                                
                                {/* Lista de pagos */}
                                {mostrarPreviaPagos && pagosPersonalizados.length > 0 && (
                                  <div className="mt-3 bg-zinc-800 rounded-lg p-3 border border-zinc-700 max-h-60 overflow-y-auto">
                                    <div className="flex justify-between items-center mb-2">
                                      <span className="text-xs text-zinc-400 font-semibold">DESGLOSE DE PAGOS</span>
                                      <span className="text-xs text-emerald-400">
                                        Total: ${pagosPersonalizados.reduce((s, p) => s + p.monto, 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                      </span>
                                    </div>
                                    <div className="space-y-2">
                                      {pagosPersonalizados.map((pago, idx) => (
                                        <div key={idx} className={`flex items-center gap-2 p-2 rounded ${pago.esAjuste ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-zinc-900'}`}>
                                          <span className="text-xs text-zinc-500 w-8">#{pago.numero}</span>
                                          <span className="flex-1 text-sm text-white font-mono">${pago.monto.toLocaleString('es-MX', {minimumFractionDigits: 2})}</span>
                                          <span className="text-xs text-zinc-500">{pago.fecha}</span>
                                          {pago.esAjuste && <span className="text-xs text-amber-400">Ajuste</span>}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}
                  </div>
              
                  <div className="bg-zinc-800 p-4 rounded-lg border border-zinc-700">
                    <Label className="text-sm font-semibold mb-3 block text-amber-500">Modo de Fechas</Label>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          id="modo_dia_fijo"
                          value="dia_fijo"
                          checked={formData.modo_fecha === 'dia_fijo' || formData.modo_fecha === 'fecha_unica'}
                          onChange={(e) => setFormData({...formData, modo_fecha: e.target.value})}
                          className="accent-amber-500"
                        />
                        <label htmlFor="modo_dia_fijo" className="text-sm cursor-pointer text-white">
                          <strong>Día fijo mensual</strong>
                          <span className="text-zinc-500 block text-xs">Cada mes en el mismo día</span>
                        </label>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <Label className="text-zinc-300">Día de Pago (1-31)</Label>
                      <Select value={formData.dia_pago} onValueChange={(value) => setFormData({...formData, dia_pago: value})}>
                        <SelectTrigger className="bg-zinc-700 border-zinc-600 text-white">
                          <SelectValue placeholder="Selecciona el día" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          {[...Array(31)].map((_, i) => (
                            <SelectItem key={i + 1} value={(i + 1).toString()} className="text-white hover:bg-zinc-700">
                              Día {i + 1}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-zinc-300">Interés por Atraso (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.interes_atraso}
                      onChange={(e) => setFormData({...formData, interes_atraso: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white"
                      placeholder="0"
                    />
                    <p className="text-xs text-zinc-500 mt-1">Penalización diaria por retraso en pagos</p>
                    {parseFloat(formData.interes_atraso) > 0 && parseFloat(formData.monto_mensualidad) > 0 && (
                      <p className="text-xs text-red-400 mt-1">
                        ${(parseFloat(formData.monto_mensualidad) * parseFloat(formData.interes_atraso) / 100).toLocaleString('es-MX', {minimumFractionDigits: 2})} por día de atraso
                      </p>
                    )}
                  </div>
                </>
              )}
              
              <div>
                <Label className="text-zinc-300">Notas (opcional)</Label>
                <Textarea
                  value={formData.notas}
                  onChange={(e) => setFormData({...formData, notas: e.target.value})}
                  rows={3}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold">
                {formData.tipo_pago === 'efectivo' ? 'Registrar Venta en Efectivo' : 'Crear Trato Financiado'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
          ) : (
            <Button disabled className="bg-zinc-800 text-zinc-500 cursor-not-allowed">
              <Lock className="w-4 h-4 mr-2" />
              Solo Lectura
            </Button>
          )}
        </div>
      </div>

      {tratosFiltrados.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-16 text-center">
          <FileText className="w-16 h-16 mx-auto mb-4 text-zinc-700" />
          <h3 className="text-lg font-bold text-white mb-2">
            {tratos.length === 0 ? 'No hay tratos registrados' : 'No hay tratos en este fraccionamiento'}
          </h3>
          <p className="text-zinc-500 mb-4">
            {tratos.length === 0 ? 'Crea el primer trato de venta' : 'Selecciona otro fraccionamiento o crea un nuevo trato'}
          </p>
          <Button onClick={() => setDialogOpen(true)} className="bg-amber-500 hover:bg-amber-400 text-black font-bold">
            <Plus className="w-4 h-4 mr-2" />
            Crear Trato
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {tratosFiltrados.map((trato) => {
            const cliente = clientes.find(c => c.cliente_id === trato.cliente_id);
            const lotesIds = trato.lotes_ids || (trato.lote_id ? [trato.lote_id] : []);
            const lotesDelTrato = lotesIds.map(id => lotes.find(l => l.lote_id === id)).filter(Boolean);
            
            let ubicacionLabel = '';
            if (lotesDelTrato.length === 1) {
              const lote = lotesDelTrato[0];
              const fracc = fraccionamientos.find(f => f.fraccionamiento_id === lote.fraccionamiento_id);
              const parts = [`Lote ${lote.numero}`];
              if (lote.manzana) parts.push(`Manzana #${lote.manzana}`);
              if (fracc) parts.push(fracc.nombre);
              ubicacionLabel = parts.join(' · ');
            } else if (lotesDelTrato.length > 1) {
              // Agrupar lotes por manzana
              const lotesNums = lotesDelTrato.map(l => l.numero).join(' y ');
              const manzanas = [...new Set(lotesDelTrato.map(l => l.manzana).filter(Boolean))];
              const fracc = fraccionamientos.find(f => f.fraccionamiento_id === lotesDelTrato[0]?.fraccionamiento_id);
              
              let label = `Lote ${lotesNums}`;
              if (manzanas.length === 1) {
                label += ` Manzana #${manzanas[0]}`;
              } else if (manzanas.length > 1) {
                label += ` Manzana #${manzanas.join(', #')}`;
              }
              if (fracc?.nombre) label += ` · ${fracc.nombre}`;
              ubicacionLabel = label;
            } else {
              ubicacionLabel = 'Sin lotes';
            }
            
            return (
              <div key={trato.trato_id} data-testid={`trato-card-${trato.trato_id}`} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 hover:border-zinc-700 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="p-3 bg-amber-500/10 rounded-lg">
                      <FileText className="w-6 h-6 text-amber-500" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs font-medium rounded">
                          {ubicacionLabel}
                        </span>
                        {lotesDelTrato.length > 1 && (
                          <span className="px-2 py-0.5 bg-zinc-700 text-zinc-300 text-xs font-medium rounded">
                            {lotesDelTrato.length} lotes
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-bold text-white">
                        {cliente?.nombre_completo || 'Cliente desconocido'}
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-4">
                        <div>
                          <p className="text-xs text-zinc-500 mb-1">
                            Precio Total 
                            <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${trato.moneda === 'MXN' ? 'bg-amber-500/20 text-amber-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                              {trato.moneda || 'USD'}
                            </span>
                          </p>
                          <p className="font-mono font-medium text-white">
                            {trato.moneda === 'MXN' ? 'MXN ' : 'USD '}${(trato.precio_final || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500 mb-1">Enganche</p>
                          <p className="font-mono font-medium text-white">${(trato.enganche || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                          {trato.metodo_pago_enganche && (
                            <p className="text-xs text-zinc-400 mt-0.5" data-testid="metodo-pago-enganche-display">
                              {({efectivo:'Efectivo',transferencia:'Transferencia',cheque:'Cheque',tarjeta:'Tarjeta'})[trato.metodo_pago_enganche] || trato.metodo_pago_enganche}
                            </p>
                          )}
                        </div>
                        <div data-testid={`trato-abono-${trato.trato_id}`}>
                          <p className="text-xs text-zinc-500 mb-1">Abono Apartado</p>
                          {(() => {
                            const lotesIds = trato.lotes_ids || (trato.lote_id ? [trato.lote_id] : []);
                            const lotesDelTratoAbono = lotesIds.map(id => lotes.find(l => l.lote_id === id)).filter(Boolean);
                            const totalAbono = lotesDelTratoAbono.reduce((sum, l) => sum + (l.abono_apartado || 0), 0);
                            const metodoAbono = lotesDelTratoAbono.find(l => l.metodo_abono)?.metodo_abono;
                            return totalAbono > 0 ? (
                              <>
                                <p className="font-mono font-medium text-cyan-400">${totalAbono.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                                {metodoAbono && <p className="text-xs text-zinc-400 mt-0.5">{({efectivo:'Efectivo',transferencia:'Transferencia',cheque:'Cheque',tarjeta:'Tarjeta'})[metodoAbono] || metodoAbono}</p>}
                              </>
                            ) : (
                              <p className="font-mono text-zinc-600">—</p>
                            );
                          })()}
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500 mb-1">Pagado</p>
                          <p className="font-mono font-medium text-emerald-400">${(trato.total_pagado || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500 mb-1">Deuda</p>
                          <p className={`font-mono font-bold ${(trato.deuda || 0) > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                            ${(trato.deuda || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500 mb-1">Pagos</p>
                          <p className="font-mono font-medium text-white">{trato.pagos_realizados || 0}/{trato.pagos_totales || trato.numero_meses}</p>
                        </div>
                      </div>
                      <div className="mt-3">
                        <div className="flex justify-between text-xs text-zinc-500 mb-1">
                          <span>Progreso</span>
                          <span>{Math.round(((trato.total_pagado || 0) / (trato.precio_final || 1)) * 100)}%</span>
                        </div>
                        <div className="w-full bg-zinc-800 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${(trato.deuda || 0) === 0 ? 'bg-emerald-500' : 'bg-amber-500'}`}
                            style={{ width: `${Math.min(100, ((trato.total_pagado || 0) / (trato.precio_final || 1)) * 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="flex items-center gap-2 text-sm text-zinc-400">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDateSafe(trato.fecha_inicio)}</span>
                    </div>
                    {(trato.deuda || 0) === 0 ? (
                      <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-bold rounded-full">
                        LIQUIDADO
                      </span>
                    ) : (
                      <span className="px-3 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">
                        PENDIENTE
                      </span>
                    )}
                    {canWrite && (
                      <div className="flex gap-2 mt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenEdit(trato)}
                          className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleteDialog({ open: true, trato: trato })}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal Editar */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) setBusquedaLoteEditar(''); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Editar Trato</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            {/* Sección de Lote */}
            <div className="bg-zinc-800 p-4 rounded-lg border border-zinc-700">
              <Label className="text-sm font-semibold mb-3 block text-cyan-400">🏠 Lote/Terreno</Label>
              {(() => {
                // Obtener el lote_id actual del trato (puede ser lote_id o lotes_ids[0])
                const loteIdOriginal = editingTrato?.lote_id || (editingTrato?.lotes_ids && editingTrato?.lotes_ids.length > 0 ? editingTrato?.lotes_ids[0] : '');
                const loteActual = editFormData.lote_id ? lotes.find(l => l.lote_id === editFormData.lote_id) : null;
                
                if (loteActual) {
                  const fracc = fraccionamientos.find(f => f.fraccionamiento_id === loteActual.fraccionamiento_id);
                  // Usar superficie o metros_cuadrados (el que tenga valor)
                  const metrosCuadrados = loteActual.superficie || loteActual.metros_cuadrados || 0;
                  return (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between bg-zinc-700 p-3 rounded-md">
                        <div>
                          <p className="text-white font-medium">
                            Manzana {loteActual.manzana || '-'}, Lote {loteActual.numero || '-'}
                          </p>
                          <p className="text-lg text-cyan-400 font-bold">
                            {metrosCuadrados} m²
                          </p>
                          <p className="text-xs text-zinc-400">
                            {fracc?.nombre || 'Sin fraccionamiento'}
                            {loteActual.medidas && ` • ${loteActual.medidas}`}
                          </p>
                        </div>
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm"
                          onClick={() => setEditFormData({...editFormData, lote_id: ''})}
                          className="border-zinc-600 text-zinc-300 hover:bg-zinc-600"
                        >
                          Cambiar
                        </Button>
                      </div>
                      <p className="text-xs text-green-400">✓ Este terreno está asignado a este trato</p>
                    </div>
                  );
                } else {
                  return (
                    <div className="space-y-2">
                      {/* Buscador de lotes para edición */}
                      <Input
                        type="text"
                        placeholder="Buscar lote por número o manzana..."
                        value={busquedaLoteEditar}
                        onChange={(e) => setBusquedaLoteEditar(e.target.value)}
                        className="bg-zinc-700 border-zinc-600 text-white placeholder:text-zinc-500"
                      />
                      <Select value={editFormData.lote_id || 'sin_lote'} onValueChange={(value) => setEditFormData({...editFormData, lote_id: value === 'sin_lote' ? '' : value})}>
                        <SelectTrigger className="bg-zinc-700 border-zinc-600 text-white">
                          <SelectValue placeholder="Seleccionar lote..." />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                          <SelectItem value="sin_lote" className="text-zinc-400 hover:bg-zinc-700">Sin lote asignado</SelectItem>
                          {/* Solo mostrar: lotes disponibles O el lote asignado a ESTE trato */}
                          {(() => {
                            const busqueda = busquedaLoteEditar.toLowerCase().trim();
                            return lotes
                              .filter(l => l.estado === 'disponible' || l.lote_id === loteIdOriginal)
                              .filter(l => {
                                if (!busqueda) return true;
                                const loteNum = String(l.numero || '').toLowerCase();
                                const manzana = String(l.manzana || '').toLowerCase();
                                return loteNum.includes(busqueda) || manzana.includes(busqueda) || 
                                       `lote ${loteNum}`.includes(busqueda) || `manzana ${manzana}`.includes(busqueda) ||
                                       `mz ${manzana}`.includes(busqueda) || l.lote_id === loteIdOriginal;
                              })
                              .sort((a, b) => {
                                // El lote actual primero
                                if (a.lote_id === loteIdOriginal) return -1;
                                if (b.lote_id === loteIdOriginal) return 1;
                                // Luego ordenar por manzana y número
                                const mzA = String(a.manzana || '');
                                const mzB = String(b.manzana || '');
                                if (mzA !== mzB) return mzA.localeCompare(mzB, undefined, {numeric: true});
                                return parseInt(a.numero || 0) - parseInt(b.numero || 0);
                              })
                              .map(lote => {
                                const fracc = fraccionamientos.find(f => f.fraccionamiento_id === lote.fraccionamiento_id);
                                const esAsignadoAEsteTrato = lote.lote_id === loteIdOriginal;
                                // Usar superficie o metros_cuadrados
                                const m2 = lote.superficie || lote.metros_cuadrados || 0;
                                return (
                                  <SelectItem key={lote.lote_id} value={lote.lote_id} className="text-white hover:bg-zinc-700">
                                    <span className="flex items-center gap-2">
                                      {esAsignadoAEsteTrato && <span className="text-green-400">●</span>}
                                      {fracc?.nombre || 'Sin fracc.'} - Mz {lote.manzana || '-'}, Lote {lote.numero || '-'} 
                                      <span className="text-cyan-400 font-semibold">({m2} m²)</span>
                                      {esAsignadoAEsteTrato && <span className="text-green-400 text-xs">(actual)</span>}
                                    </span>
                                  </SelectItem>
                                );
                              });
                          })()}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-zinc-500">Solo se muestran lotes disponibles o el asignado a este trato</p>
                    </div>
                  );
                }
              })()}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Precio Final</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editFormData.precio_final}
                  onChange={(e) => setEditFormData({...editFormData, precio_final: e.target.value})}
                  required
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Enganche</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editFormData.enganche}
                  onChange={(e) => setEditFormData({...editFormData, enganche: e.target.value})}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder="0 si no hay enganche"
                />
              </div>
              {/* Solo mostrar fecha de enganche si hay monto */}
              {parseFloat(editFormData.enganche) > 0 && (
                <div>
                  <Label className="text-zinc-300">📅 Fecha del Enganche</Label>
                  <Input
                    type="date"
                    value={editFormData.fecha_enganche}
                    onChange={(e) => setEditFormData({...editFormData, fecha_enganche: e.target.value})}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Fecha del Trato</Label>
                <Input
                  type="date"
                  value={editFormData.fecha_inicio}
                  onChange={(e) => setEditFormData({...editFormData, fecha_inicio: e.target.value})}
                  required
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
                {/* Advertencia cuando la fecha cambia */}
                {editFormData.fecha_inicio !== fechaInicioOriginal && fechaInicioOriginal && (
                  <div className="mt-2 p-2 bg-amber-500/20 border border-amber-500/50 rounded-md">
                    <p className="text-xs text-amber-400 font-medium">
                      ⚠️ Al cambiar la fecha, TODOS los pagos serán reiniciados y tendrás que volver a confirmarlos.
                    </p>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-zinc-300">📅 Fecha Primera Mensualidad</Label>
                <Input
                  type="date"
                  value={editFormData.fecha_primera_mensualidad}
                  onChange={(e) => setEditFormData({...editFormData, fecha_primera_mensualidad: e.target.value})}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
                <p className="text-xs text-zinc-500 mt-1">Primer pago después del enganche</p>
              </div>
            </div>
            
            <div className="bg-zinc-800 p-4 rounded-lg border border-zinc-700">
              <Label className="text-sm font-semibold mb-3 block text-amber-500">Modo de Cálculo</Label>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <input type="radio" value="meses_fijos" checked={editFormData.modo_calculo === 'meses_fijos'} onChange={(e) => setEditFormData({...editFormData, modo_calculo: e.target.value})} className="accent-amber-500" />
                  <label className="text-sm text-white">Meses fijos</label>
                </div>
                <div className="flex items-center gap-3">
                  <input type="radio" value="mensualidad_fija" checked={editFormData.modo_calculo === 'mensualidad_fija'} onChange={(e) => setEditFormData({...editFormData, modo_calculo: e.target.value})} className="accent-amber-500" />
                  <label className="text-sm text-white">Mensualidad fija</label>
                </div>
              </div>
              
              {editFormData.modo_calculo === 'meses_fijos' ? (
                <div className="mt-4">
                  <Label className="text-zinc-300">Número de Meses</Label>
                  <Input type="number" min="1" value={editFormData.numero_meses} onChange={(e) => setEditFormData({...editFormData, numero_meses: e.target.value})} required className="bg-zinc-700 border-zinc-600 text-white" />
                </div>
              ) : (
                <div className="mt-4">
                  <Label className="text-zinc-300">Monto Mensualidad</Label>
                  <Input type="number" step="0.01" min="1" value={editFormData.monto_mensualidad} onChange={(e) => setEditFormData({...editFormData, monto_mensualidad: e.target.value})} required className="bg-zinc-700 border-zinc-600 text-white" />
                </div>
              )}
              
              {/* Botón para ver desglose de pagos en edición */}
              {editFormData.precio_final && (editFormData.monto_mensualidad || editFormData.numero_meses) && (
                <div className="mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      calcularDesglosePagosEdit();
                      setMostrarDesglosePagosEdit(!mostrarDesglosePagosEdit);
                    }}
                    className="w-full border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                  >
                    {mostrarDesglosePagosEdit ? 'Ocultar' : 'Ver'} desglose de pagos
                    {(() => {
                      const montoFinanciado = parseFloat(editFormData.precio_final || 0) - parseFloat(editFormData.enganche || 0);
                      if (editFormData.modo_calculo === 'mensualidad_fija' && editFormData.monto_mensualidad) {
                        return ` (${Math.ceil(montoFinanciado / parseFloat(editFormData.monto_mensualidad))} pagos)`;
                      } else if (editFormData.numero_meses) {
                        return ` (${editFormData.numero_meses} pagos)`;
                      }
                      return '';
                    })()}
                  </Button>
                  
                  {/* Desglose de pagos */}
                  {mostrarDesglosePagosEdit && desglosePagosEdit.length > 0 && (
                    <div className="mt-3 bg-zinc-900 rounded-lg p-3 border border-zinc-700 max-h-60 overflow-y-auto">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs text-zinc-400 font-semibold">DESGLOSE DE PAGOS</span>
                        <span className="text-xs text-emerald-400">
                          Total: ${desglosePagosEdit.reduce((s, p) => s + p.monto, 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="space-y-1">
                        {desglosePagosEdit.map((pago, idx) => (
                          <div key={idx} className={`flex items-center justify-between p-2 rounded text-sm ${pago.esUltimo ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-zinc-800'}`}>
                            <span className="text-zinc-400">#{pago.numero}</span>
                            <span className="text-white font-medium">${pago.monto.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                            <span className="text-zinc-500 text-xs">{pago.fecha}</span>
                            {pago.esUltimo && <span className="text-amber-400 text-xs">Ajuste</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="bg-zinc-800 p-4 rounded-lg border border-zinc-700">
              <Label className="text-sm font-semibold mb-3 block text-amber-500">Día de Pago Mensual</Label>
              <div className="mt-2">
                <Label className="text-zinc-300">Día de Pago</Label>
                <Select value={editFormData.dia_pago} onValueChange={(value) => setEditFormData({...editFormData, dia_pago: value})}>
                  <SelectTrigger className="bg-zinc-700 border-zinc-600 text-white"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {[...Array(31)].map((_, i) => (
                      <SelectItem key={i + 1} value={(i + 1).toString()} className="text-white hover:bg-zinc-700">Día {i + 1}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-500 mt-1">Día del mes en que vence cada mensualidad</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Interés por Atraso (%)</Label>
                <Input type="number" step="0.01" value={editFormData.interes_atraso} onChange={(e) => setEditFormData({...editFormData, interes_atraso: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" placeholder="0" />
                <p className="text-xs text-zinc-500 mt-1">Penalización diaria por retraso</p>
                {parseFloat(editFormData.interes_atraso) > 0 && parseFloat(editFormData.monto_mensualidad || (parseFloat(editFormData.precio_final) - parseFloat(editFormData.enganche || 0)) / parseFloat(editFormData.numero_meses || 1)) > 0 && (
                  <p className="text-xs text-amber-400 mt-1">
                    ${(parseFloat(editFormData.monto_mensualidad || (parseFloat(editFormData.precio_final) - parseFloat(editFormData.enganche || 0)) / parseFloat(editFormData.numero_meses || 1)) * parseFloat(editFormData.interes_atraso) / 100).toLocaleString('es-MX', {minimumFractionDigits: 2})} por día de atraso
                  </p>
                )}
              </div>
              <div>
                <Label className="text-zinc-300">Interés de Venta (%)</Label>
                <Input type="number" step="0.01" value={editFormData.interes_venta} onChange={(e) => setEditFormData({...editFormData, interes_venta: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" placeholder="0" />
                <p className="text-xs text-zinc-500 mt-1">Interés mensual sobre el financiamiento</p>
                {parseFloat(editFormData.interes_venta) > 0 && parseFloat(editFormData.precio_final) > 0 && parseFloat(editFormData.numero_meses) > 0 && (
                  <p className="text-xs text-amber-400 mt-1">
                    +${((parseFloat(editFormData.precio_final) - parseFloat(editFormData.enganche || 0)) * (parseFloat(editFormData.interes_venta) / 100) * parseFloat(editFormData.numero_meses)).toLocaleString('es-MX', {minimumFractionDigits: 2})} de interés total ({editFormData.numero_meses} meses)
                  </p>
                )}
              </div>
            </div>
            
            <div>
              <Label className="text-zinc-300">Notas</Label>
              <Textarea value={editFormData.notas} onChange={(e) => setEditFormData({...editFormData, notas: e.target.value})} rows={3} className="bg-zinc-800 border-zinc-700 text-white" />
            </div>
            
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1 border-zinc-700 text-white hover:bg-zinc-800">Cancelar</Button>
              <Button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-bold">Guardar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* MODAL PANTALLA GRANDE - Tabla de Amortización */}
      <Dialog open={modalAmortizacionAbierto} onOpenChange={setModalAmortizacionAbierto}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="border-b border-zinc-800 pb-4">
            <div className="flex justify-between items-center">
              <DialogTitle className="text-white text-xl">📊 Tabla de Amortización Completa</DialogTitle>
            </div>
          </DialogHeader>
          
          {tablaAmortizacion && (
            <div className="flex-1 overflow-y-auto">
              {/* Resumen en grande */}
              <div className="p-6 bg-zinc-800/50 border-b border-zinc-700">
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                  <div className="text-center p-4 bg-zinc-900 rounded-lg">
                    <p className="text-sm text-zinc-500 mb-1">Saldo a Financiar</p>
                    <p className="text-2xl font-bold text-white">
                      ${(parseFloat(formData.precio_final || 0) - parseFloat(formData.enganche || 0)).toLocaleString('es-MX', {minimumFractionDigits: 2})}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-zinc-900 rounded-lg">
                    <p className="text-sm text-zinc-500 mb-1">Tasa Mensual</p>
                    <p className="text-2xl font-bold text-amber-400">{formData.interes_venta}%</p>
                  </div>
                  <div className="text-center p-4 bg-zinc-900 rounded-lg">
                    <p className="text-sm text-zinc-500 mb-1">Mensualidad</p>
                    <p className="text-2xl font-bold text-cyan-400">
                      ${(formData.modo_calculo === 'meses_fijos' ? mensualidadCalculada : parseFloat(formData.monto_mensualidad || 0))?.toLocaleString('es-MX', {minimumFractionDigits: 2})}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-zinc-900 rounded-lg">
                    <p className="text-sm text-zinc-500 mb-1">Total de Meses</p>
                    <p className="text-2xl font-bold text-white">{tablaAmortizacion.total_meses}</p>
                  </div>
                  <div className="text-center p-4 bg-zinc-900 rounded-lg">
                    <p className="text-sm text-zinc-500 mb-1">Total Interés</p>
                    <p className="text-2xl font-bold text-amber-400">${tablaAmortizacion.total_interes?.toLocaleString('es-MX', {minimumFractionDigits: 2})}</p>
                  </div>
                  <div className="text-center p-4 bg-zinc-900 rounded-lg">
                    <p className="text-sm text-zinc-500 mb-1">Total a Pagar</p>
                    <p className="text-2xl font-bold text-emerald-400">${tablaAmortizacion.total_pagado?.toLocaleString('es-MX', {minimumFractionDigits: 2})}</p>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <p className="text-zinc-400">
                    📅 Fecha de Liquidación: <span className="text-cyan-400 font-bold">{formatDateSafe(tablaAmortizacion.fecha_liquidacion)}</span>
                  </p>
                </div>
              </div>
              
              {/* Tabla detallada en grande */}
              <div className="p-6">
                <h3 className="text-lg font-bold text-white mb-4">Desglose Mensual de Pagos</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-800 sticky top-0">
                      <tr className="text-zinc-400">
                        <th className="p-3 text-left font-semibold"># Pago</th>
                        <th className="p-3 text-left font-semibold">Fecha</th>
                        <th className="p-3 text-right font-semibold">Saldo Inicial</th>
                        <th className="p-3 text-right font-semibold">Interés ({formData.interes_venta}%)</th>
                        <th className="p-3 text-right font-semibold">Capital</th>
                        <th className="p-3 text-right font-semibold">Pago Total</th>
                        <th className="p-3 text-right font-semibold">Saldo Final</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tablaAmortizacion.schedule?.map((pago, idx) => (
                        <tr 
                          key={idx} 
                          className={`border-t border-zinc-800 hover:bg-zinc-800/50 transition-colors ${pago.saldo_final === 0 ? 'bg-emerald-500/10' : ''}`}
                        >
                          <td className="p-3 text-zinc-400 font-mono">{pago.numero_pago}</td>
                          <td className="p-3 text-white">{formatDateSafe(pago.fecha_programada)}</td>
                          <td className="p-3 text-right text-zinc-300 font-mono">${pago.saldo_inicial?.toLocaleString('es-MX', {minimumFractionDigits: 2})}</td>
                          <td className="p-3 text-right text-amber-400 font-mono">${pago.interes_mes?.toLocaleString('es-MX', {minimumFractionDigits: 2})}</td>
                          <td className="p-3 text-right text-cyan-400 font-mono">${pago.capital_pagado?.toLocaleString('es-MX', {minimumFractionDigits: 2})}</td>
                          <td className={`p-3 text-right font-mono font-bold ${pago.saldo_final === 0 ? 'text-emerald-400' : 'text-white'}`}>
                            ${pago.pago_total?.toLocaleString('es-MX', {minimumFractionDigits: 2})}
                          </td>
                          <td className={`p-3 text-right font-mono ${pago.saldo_final === 0 ? 'text-emerald-400 font-bold' : 'text-zinc-300'}`}>
                            ${pago.saldo_final?.toLocaleString('es-MX', {minimumFractionDigits: 2})}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-zinc-800 font-bold">
                      <tr className="border-t-2 border-zinc-600">
                        <td className="p-3 text-white" colSpan={3}>TOTALES</td>
                        <td className="p-3 text-right text-amber-400">${tablaAmortizacion.total_interes?.toLocaleString('es-MX', {minimumFractionDigits: 2})}</td>
                        <td className="p-3 text-right text-cyan-400">
                          ${(parseFloat(formData.precio_final || 0) - parseFloat(formData.enganche || 0)).toLocaleString('es-MX', {minimumFractionDigits: 2})}
                        </td>
                        <td className="p-3 text-right text-emerald-400">${tablaAmortizacion.total_pagado?.toLocaleString('es-MX', {minimumFractionDigits: 2})}</td>
                        <td className="p-3 text-right text-emerald-400">$0.00</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          )}
          
          <div className="border-t border-zinc-800 p-4 flex justify-end">
            <Button
              onClick={() => setModalAmortizacionAbierto(false)}
              className="bg-zinc-700 hover:bg-zinc-600 text-white"
            >
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AlertDialog Eliminar */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">¿Mover a papelera?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              El trato será movido a la papelera. Podrás restaurarlo o eliminarlo permanentemente desde ahí.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">Mover a Papelera</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
